<script lang="ts">
import { defineComponent } from "vue";

export default defineComponent({
    components: {},
    props: {
        value: {
            type: Number,
            default: 0,
        },
        min: {
            type: Number,
            default: 0,
        },
        max: {
            type: Number,
            default: 5,
        },
        step: {
            type: Number,
            default: 1,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        alternate: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["change"],
    data: () => ({
        internalValue: 0,
    }),
    computed: {
        valueCount(): number {
            return Math.floor((this.max - this.min) / this.step) + 1;
        },

        maxIndex(): number {
            return this.valueCount - 1;
        },
        currentIndex(): number {
            return this.valueToIndex(this.internalValue);
        },
        zeroIndex(): number {
            return this.valueToIndex(0);
        },

        activeStartIndex(): number {
            return Math.min(this.zeroIndex, this.currentIndex);
        },
        activeEndIndex(): number {
            return Math.max(this.zeroIndex, this.currentIndex);
        },

        dots(): {
            index: number;
            active: boolean;
            style: Record<string, string>;
        }[] {
            return Array.from({ length: this.valueCount }, (_, index: number) => {
                return {
                    index,
                    active: index >= this.activeStartIndex && index <= this.activeEndIndex,
                    style: {
                        left: `${this.indexToPercent(index)}%`,
                    },
                };
            });
        },
        segments(): {
            index: number;
            active: boolean;
            style: Record<string, string>;
        }[] {
            return Array.from({ length: this.valueCount - 1 }, (_, index: number) => {
                return {
                    index,
                    active: index >= this.activeStartIndex && index < this.activeEndIndex,
                    style: {
                        left: `calc(${this.indexToPercent(index)}% + var(--segment-gap))`,
                        width: `calc(${100 / this.maxIndex}% - (var(--segment-gap) * 2))`,
                    },
                };
            });
        },
    },
    watch: {
        value: {
            immediate: true,
            handler(value: number): void {
                this.internalValue = this.clampValue(value);
            },
        },
    },
    methods: {
        onInput(event: Event): void {
            if (this.disabled) return;

            this.internalValue = Number((event.target as HTMLInputElement).value);
            this.$emit("change", this.internalValue);
        },

        // helpers
        valueToIndex(value: number): number {
            return Math.round((this.clampValue(value) - this.min) / this.step);
        },
        indexToPercent(index: number): number {
            if (this.maxIndex <= 0) return 0;

            return (index / this.maxIndex) * 100;
        },
        clampValue(value: number): number {
            return Math.min(this.max, Math.max(this.min, value));
        },
    },
});
</script>

<template>
    <div class="segmented-slider" :class="{ alternate, disabled }">
        <div class="segmented-slider-track">
            <div v-for="segment in segments" :key="segment.index" class="segmented-slider-segment" :class="{ active: segment.active }" :style="segment.style"></div>

            <div v-for="dot in dots" :key="dot.index" class="segmented-slider-dot" :class="{ active: dot.active }" :style="dot.style"></div>
        </div>

        <input class="segmented-slider-input" type="range" :min="min" :max="max" :step="step" :value="internalValue" :disabled="disabled" @input="onInput" />
    </div>
</template>

<style lang="scss" scoped>
.segmented-slider {
    position: relative;
    width: 100%;
    min-width: #{rem(20)};
    min-height: #{rem(50)};

    --thumb-size: #{rem(35)};
    --dot-size: #{rem(13)};
    --track-height: #{rem(5)};
    --inactive-color: #{$color-accent-gray-a};
    --active-color: #{$color-primary-a};
    --active-color-hover: #{$color-primary-b};
    --segment-gap: #{rem(10)};

    &.alternate {
        --inactive-color: transparent;
        --active-color: #{$color-secondary-a};
        --active-color-hover: #{$color-secondary-b};
    }

    &.disabled {
        pointer-events: none;
        & * {
            pointer-events: none;
        }
    }

    .segmented-slider-track {
        position: absolute;
        left: calc(var(--thumb-size) / 2);
        right: calc(var(--thumb-size) / 2);
        top: 50%;
        height: var(--dot-size);
        transform: translateY(-50%);
        pointer-events: none;
    }

    .segmented-slider-segment {
        position: absolute;
        top: 50%;
        height: var(--track-height);
        transform: translateY(-50%);
        background-color: var(--inactive-color);
        transition: background-color 0.1s ease;

        &.active {
            background-color: var(--active-color);
        }
    }

    .segmented-slider-dot {
        position: absolute;
        top: 50%;
        width: var(--dot-size);
        height: var(--dot-size);
        border-radius: 50%;
        transform: translate(-50%, -50%);
        background-color: var(--inactive-color);

        &.active {
            background-color: var(--active-color);
        }
    }

    .segmented-slider-input {
        position: relative;
        z-index: 2;
        width: 100%;
        height: 100%;
        margin: 0;
        appearance: none;
        background: transparent;
        cursor: pointer;

        &::-webkit-slider-runnable-track {
            height: rem(50);
            background: transparent;
        }

        &::-webkit-slider-thumb {
            appearance: none;
            width: var(--thumb-size);
            height: var(--thumb-size);
            margin-top: rem(9);
            border: solid rem(5) $color-accent-light-a;
            border-radius: 50%;
            background-color: var(--active-color);
            cursor: grab;

            transition:
                background-color 0.12s ease,
                transform 0.12s ease;
            // will-change: transform;

            &:hover {
                background-color: var(--active-color-hover);
                transform: scale(1.2);
            }
        }

        &::-moz-range-track {
            height: rem(50);
            background: transparent;
        }

        &::-moz-range-thumb {
            width: var(--thumb-size);
            height: var(--thumb-size);
            border: none;
            border-radius: 50%;
            background-color: var(--active-color);
            cursor: grab;
        }
    }
}

@include media-breakpoint-down(lg) {
    .segmented-slider {
        --thumb-size: #{rem(30)};
        --dot-size: #{rem(12)};
        --track-height: #{rem(4)};
        --segment-gap: #{rem(10)};

        .segmented-slider-input {
            &::-webkit-slider-thumb {
                margin-top: rem(11);
                border-width: rem(4);
            }
        }
    }
}

@include media-breakpoint-down(md) {
    .segmented-slider {
        min-height: rem(42);

        --thumb-size: #{rem(24)};
        --dot-size: #{rem(11)};
        --track-height: #{rem(3)};
        --segment-gap: #{rem(9)};

        .segmented-slider-input {
            &::-webkit-slider-runnable-track {
                height: rem(42);
            }

            &::-moz-range-track {
                height: rem(42);
            }

            &::-webkit-slider-thumb {
                margin-top: rem(10);
                border-width: rem(4);
            }
        }
    }
}

@include media-breakpoint-down(sm) {
    .segmented-slider {
        min-height: rem(36);

        --thumb-size: #{rem(22)};
        --dot-size: #{rem(8)};
        --segment-gap: #{rem(8)};

        .segmented-slider-input {
            &::-webkit-slider-runnable-track {
                height: rem(36);
            }

            &::-moz-range-track {
                height: rem(36);
            }

            &::-webkit-slider-thumb {
                margin-top: rem(9);
                border-width: rem(4);
            }
        }
    }
}
</style>
